import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClaimClosure,
  ClaimClosuresPage,
  ClaimReopenResult,
} from '@evogard/contract';
import {
  loadFailureMessage,
  type ClaimClosureKind,
  type ClaimClosureOrigin,
} from '@evogard/domain';
import { api } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';

/**
 * A conversa dos Encerrados com a API (`/claims/closures`, 13/10/2026).
 *
 * **Reabrir invalida `['sinistros']` inteiro**, e não só a lista daqui: o
 * reaberto volta ao quadro do departamento (ou de Periféricos), muda a ficha e
 * ganha uma linha na Atividade — e as chaves de todas essas telas começam com
 * `sinistros`, o id do módulo no menu.
 */

const ROOT = ['sinistros', 'encerrados'] as const;

/** A recusa da API, com o código dela — a tela decide pelo código, e não pela frase. */
export class ClosureRefusal extends Error {
  constructor(
    message: string,
    readonly code: string | null,
  ) {
    super(message);
  }
}

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export function useClosurePermissions() {
  const { data: context } = useSessionContext();
  return {
    canReopen: can(context, 'claims.closures', 'reabrir'),
    /* "Ver ficha" do sinistro abre `/claims/$id`, que cobra `claims.notices:ver`. */
    canOpenClaim: can(context, 'claims.notices', 'ver'),
    canOpenPeripheral: can(context, 'claims.peripherals', 'ver'),
  };
}

export interface ClosureFilter {
  kind?: ClaimClosureKind;
  origin?: ClaimClosureOrigin;
  search?: string;
  /** `AAAA-MM-DD`. Sem ele, a API aplica os últimos 90 dias e diz de quando. */
  since?: string;
  until?: string;
}

export function useClosureList(filter: ClosureFilter, page: number, perPage: number) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'lista', tenantId, filter, page, perPage],
    queryFn: async (): Promise<ClaimClosuresPage> => {
      const r = await api.claimClosures.listClaimClosures({
        query: {
          page,
          perPage,
          ...(filter.kind ? { kind: filter.kind } : {}),
          ...(filter.origin ? { origin: filter.origin } : {}),
          ...(filter.search ? { search: filter.search } : {}),
          ...(filter.since ? { since: filter.since } : {}),
          ...(filter.until ? { until: filter.until } : {}),
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os encerrados'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: (previous) => previous,
  });
}

/**
 * O que reabrir precisa saber do encerrado: quem é, onde está e de onde saiu
 * (`fromPhase` só para dizer na tela para onde ele volta — nula, a frase é
 * genérica). A linha de Encerrados serve, e a mesa monta o seu.
 */
export type ReopenTarget = Pick<
  ClaimClosure,
  'source' | 'id' | 'origin' | 'phase' | 'fromPhase'
>;

/** Reabrir o sinistro ou o acionamento pelo ato que o desfecho aceita. */
export function useReopenClosure() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: {
      closure: ReopenTarget;
      reason: string;
    }): Promise<ClaimReopenResult> => {
      const args = {
        params: { id: a.closure.id },
        body: { fromPhase: a.closure.phase, reason: a.reason },
      };
      const r =
        a.closure.source === 'periferico'
          ? await api.claimClosures.reopenPeripheral(args)
          : await api.claimClosures.reopenClaim(args);
      if (r.status !== 200) {
        const body = r.body as { message?: string; code?: string } | undefined;
        throw new ClosureRefusal(
          body?.message ?? 'Não foi possível reabrir.',
          body?.code ?? null,
        );
      }
      return r.body;
    },
    /* Também na recusa: o 409 de `fase_mudou` diz que a lista está velha. */
    onSettled: () => client.invalidateQueries({ queryKey: ['sinistros'] }),
  });
}
